"use client";

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import "@/components/Article/articleMarkdown.css";

/* =======================
   SANITIZE SCHEMA (SAFE)
   ======================= */

const sanitizeSchema = {
  ...defaultSchema,

  tagNames: [
    ...(defaultSchema.tagNames || []),
    "section",
    "div",
    "span",
    "a",
    "img",
    "br",
    "blockquote",
    "iframe",
  ],

  attributes: {
    ...(defaultSchema.attributes || {}),

    // Use HTML attribute names (not React props like className)
    section: ["class", "id"],
    div: ["class", "id"],
    span: ["class", "id"],

    a: ["href", "target", "rel", "class", "id"],

    // Allow common image attributes and CKEditor saved-src attributes
    img: [
      "src",
      "alt",
      "title",
      "loading",
      "decoding",
      "class",
      "data-src",
      "data-cke-saved-src",
    ],
    blockquote: ["class", "id"],
    iframe: [
      "src",
      "width",
      "height",
      "allow",
      "allowfullscreen",
      "frameborder",
      "title",
      "class",
    ],

    "*": ["class", "id"],
  },

  protocols: {
    href: ["http", "https", "mailto", "tel"],
    // Allow absolute http/https, root-relative (empty string), data URIs and blob urls
    src: ["http", "https", "", "data", "blob"],
  },
};

/* =======================
   PREPROCESS MARKDOWN
   ======================= */

const preprocessMarkdown = (content) => {
  if (content == null) return "";

  // Coerce common object-shaped editor payloads into strings safely
  let str = content;
  if (typeof content !== "string") {
    if (content && typeof content.html === "string") {
      str = content.html;
    } else if (content && typeof content.data === "string") {
      // CKEditor commonly provides { data: '<p>...</p>' }
      str = content.data;
    } else if (content && typeof content.value === "string") {
      str = content.value;
    } else if (content && typeof content.Editor === "string") {
      str = content.Editor;
    } else if (content && typeof content.editor === "string") {
      str = content.editor;
    } else {
      try {
        str = JSON.stringify(content);
      } catch (e) {
        str = String(content);
      }
    }
  }

  return str
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\n{2,}/g, (match) => {
      const n = match.length;
      if (n === 2) return "\n\n";
      return `\n\n${"<br>".repeat(n - 2)}\n\n`;
    })
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, "\n\n![$1]($2)\n\n")
    .replace(/^(#{1,6}\s.+)$/gm, "\n\n$1\n\n")
    .trim();
};

/* =======================
   CUSTOM RENDERERS
   ======================= */

// Helper: extract plain text from React children (recursively)
const getTextFromChildren = (children) =>
  React.Children.toArray(children)
    .map((c) => {
      if (typeof c === "string") return c;
      if (c == null) return "";
      if (c.props && c.props.children)
        return getTextFromChildren(c.props.children);
      return "";
    })
    .join("");

const stripMarkdown = (s) =>
  (s || "")
    .toString()
    .replace(/\*\*|__|\*|_|`/g, "")
    .replace(/\[([^\]]+)\]\([^\)]+\)/g, "$1")
    .trim();

const components = {
  p: ({ node, children }) => {
    if (!children || children.length === 0) return null;

    // Image-only paragraph
    if (node?.children?.length === 1 && node.children[0].tagName === "img") {
      return <>{children}</>;
    }

    // Mixed content with image
    const hasImage = node?.children?.some((c) => c.tagName === "img");
    if (hasImage) {
      return <div className="content-block mb-4">{children}</div>;
    }

    return <p className="mb-4 leading-relaxed">{children}</p>;
  },

  /* ✅ FIXED BULLET LISTS */
  ul: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,

  ol: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,

  li: ({ children }) => <li className="mb-1 leading-relaxed">{children}</li>,

  img: ({ src, alt, title }) =>
    // Avoid rendering images with empty src which can trigger a network reload warning
    (() => {
      const s = typeof src === "string" ? src.trim() : src;
      if (!s) return null;

      return (
        <div className="image-container my-6">
          <img
            src={s}
            alt={alt || ""}
            title={title}
            loading="lazy"
            decoding="async"
            className="max-w-full h-auto mx-auto block rounded-lg shadow-sm"
          />
        </div>
      );
    })(),

  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-gray-300 pl-4 my-4 italic text-gray-700">
      {children}
    </blockquote>
  ),

  iframe: ({ src, title }) => {
    // Allow trusted iframe sources (http(s), data:, blob:). If you need to restrict
    // to specific hosts, update the check below (e.g. only youtube/vimeo).
    if (!src) return null;

    const s = typeof src === "string" ? src.trim() : src;
    if (!s) return null;

    // Only render iframe for absolute http(s), data or blob URLs
    const allowed =
      s.startsWith("http://") ||
      s.startsWith("https://") ||
      s.startsWith("data:") ||
      s.startsWith("blob:");
    if (!allowed) return null;

    return (
      <div className="relative my-8 w-full aspect-video">
        <iframe
          src={s}
          title={title || "Embedded content"}
          className="absolute inset-0 w-full h-full rounded-lg"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  },

  h1: ({ children }) => {
    const raw = getTextFromChildren(children);
    const text = stripMarkdown(raw);
    const id = text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/<[^>]+>/g, "")
      .replace(/[\s]+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");

    return (
      <h1 id={id} className="text-3xl font-bold mt-8 mb-4 first:mt-0">
        {children}
      </h1>
    );
  },

  h2: ({ children }) => {
    const raw = getTextFromChildren(children);
    const text = stripMarkdown(raw);
    const id = text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/<[^>]+>/g, "")
      .replace(/[\s]+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");

    return (
      <h2 id={id} className="text-2xl font-bold mt-6 mb-3">
        {children}
      </h2>
    );
  },

  h3: ({ children }) => {
    const raw = getTextFromChildren(children);
    const text = stripMarkdown(raw);
    const id = text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/<[^>]+>/g, "")
      .replace(/[\s]+/g, "-")
      .replace(/[^a-z0-9\-]/g, "");

    return (
      <h3 id={id} className="text-xl font-semibold mt-4 mb-2">
        {children}
      </h3>
    );
  },

  br: () => <br className="block my-2" />,
};

/* =======================
   COMPONENT
   ======================= */

const ArticleMarkdown = ({ markdown }) => {
  const processed = preprocessMarkdown(markdown);

  return (
    <div className="article-content markdown-container prose max-w-none">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={components}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
};

export default ArticleMarkdown;
